import camelCaseKeys from "camelcase-keys";
import snakecaseKeys from "snakecase-keys";
import { z } from "zod";

import { ApiResponseError } from "Helpers/ApiRequest";

import {
  authorizedGet,
  authorizedPost,
  authorizedDelete,
  authorizedPatch,
} from "../../authorizedRequests";
import { Venue } from "../../schema";
import { CustomerTab } from "../CustomerTab/schema";
import { SoldOutItemsError } from "./errors";
import {
  CartCheckoutSessionResponseSchema,
  CartSchema,
  CartStatus,
  CheckoutErrorSchema,
} from "./schema";
import type { Cart } from "./schema";

type CartRequest = Pick<
  Cart,
  | "uuid"
  | "name"
  | "venueId"
  | "floorTableId"
  | "customerTabId"
  | "pickUpAt"
  | "pickUpInMinutes"
  | "pickUpMethod"
>;
interface CartFilters {
  status: CartStatus;
  venueId: string;
  customerTabId: string;
}

export const fetchCarts = async (
  filter: Partial<CartFilters>,
): Promise<Cart[]> => {
  const params = new URLSearchParams(snakecaseKeys(filter));

  const response = await authorizedGet({
    url: `/v1/carts?${params.toString()}`,
  });
  const responseData = camelCaseKeys(response.body.carts, {
    deep: true,
  });

  return responseData.map(CartSchema.parse);
};

export const fetchCart = async (cartUuid: string): Promise<Cart> => {
  const response = await authorizedGet({
    url: `/v1/carts/${cartUuid}`,
  });
  const responseData = camelCaseKeys(response.body.cart, {
    deep: true,
  });

  try {
    console.log(
      CartSchema.safeParse(camelCaseKeys(responseData, { deep: true })).error,
    );
    return CartSchema.parse(camelCaseKeys(responseData, { deep: true }));
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const createCart = async (cart: Partial<CartRequest>): Promise<Cart> => {
  const response = await authorizedPost({
    url: "/v1/carts",
    body: snakecaseKeys({ cart }, { deep: true }),
  });
  return CartSchema.parse(camelCaseKeys(response.body.cart, { deep: true }));
};

export const updateCart = async (
  cart: Partial<CartRequest> & { uuid: string },
): Promise<Cart> => {
  const response = await authorizedPatch({
    url: `/v1/carts/${cart.uuid}`,
    body: snakecaseKeys({ cart }, { deep: true }),
  });
  return CartSchema.parse(camelCaseKeys(response.body.cart, { deep: true }));
};

export const deleteCart = async (cartUuid: string) => {
  const { body } = await authorizedDelete({
    url: `/v1/carts/${cartUuid}`,
    body: {},
  });
  return CartSchema.parse(camelCaseKeys(body.cart, { deep: true }));
};
const CheckoutResponseSchema = z.object({
  checkoutUrl: z.string(),
});
export const deleteSession = async () => {
  await authorizedDelete({
    url: `/v1/mobile_order_sessions`,
    body: {},
  });
  return true;
};

export const createNextCart = async ({
  customerTab,
  venue,
  lastCart,
}: {
  customerTab?: CustomerTab;
  venue: Venue;
  lastCart?: Cart;
}): Promise<Cart> => {
  const response = await createCart({
    venueId: venue.uuid,
    customerTabId: customerTab?.uuid || lastCart?.customerTabId || undefined,
    floorTableId: customerTab?.tableUuid || lastCart?.floorTableId || undefined,
    name: customerTab?.name || lastCart?.name || undefined,
  });
  return response;
};

export const createCartCheckoutSession = async (CartUuid: string) => {
  try {
    const { body } = await authorizedPost({
      url: `/v1/carts/${CartUuid}/checkout_sessions`, // THIS IS DELIBRATELY WRONG
      body: {},
    });
    return CartCheckoutSessionResponseSchema.parse(
      camelCaseKeys(body, { deep: true }),
    );
  } catch (e) {
    if (e instanceof ApiResponseError) {
      const error = CheckoutErrorSchema.parse(
        camelCaseKeys(e.body, { deep: true }),
      );
      console.log({ error, body: e.body });
      if (error.error === "items_sold_out") {
        console.error("Items are sold out", error.soldOutItemsUuids);
        throw new SoldOutItemsError(error.message, error.soldOutItemsUuids);
      }
      throw new Error(error.message);
    }
    console.error(e);
    throw e;
  }
};

export const finalizeCartOrder = async (CartUuid: string): Promise<Cart> => {
  try {
    const { body } = await authorizedPatch({
      url: `/v1/carts/${CartUuid}/finalize`,
      body: {},
    });
    const cart = CartSchema.parse(camelCaseKeys(body.cart, { deep: true }));
    return cart;
  } catch (e) {
    if (e instanceof ApiResponseError) {
      const error = CheckoutErrorSchema.parse(
        camelCaseKeys(e.body, { deep: true }),
      );
      console.log({ error, body: e.body });
      if (error.error === "items_sold_out") {
        console.error("Items are sold out", error.soldOutItemsUuids);
        throw new SoldOutItemsError(error.message, error.soldOutItemsUuids);
      }
      throw new Error(error.message);
    }
    console.error(e);
    throw e;
  }
};

interface TabCheckoutSessionRequest {
  billSplitCustomerSessionId: string;
}
export const createTabCheckoutSession = async (
  customerTabUuid: string,
  request?: Partial<TabCheckoutSessionRequest>,
) => {
  console.log("Checkout Session called");
  const { body } = await authorizedPost({
    url: `/v1/customer_tabs/${customerTabUuid}/checkout_sessions`,
    body: snakecaseKeys(request || {}),
  });
  const { checkoutUrl } = CheckoutResponseSchema.parse(
    camelCaseKeys(body, { deep: true }),
  );
  return checkoutUrl;
};
